import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile invisible widget.
 *
 * Props:
 *  - onToken(token)  fired when challenge passes (only argument is the token string)
 *  - onError(msg)    fired when challenge fails / expires / times out
 *
 * The widget is invisible — legitimate users see nothing. If
 * REACT_APP_TURNSTILE_SITE_KEY is missing, the component is a no-op and calls
 * onToken("") on mount so dev environments still work.
 */
let scriptInjected = false;
let scriptReadyPromise = null;

function loadTurnstileScript() {
  if (scriptReadyPromise) return scriptReadyPromise;

  scriptReadyPromise = new Promise((resolve) => {
    if (typeof window === "undefined") return;
    if (window.turnstile) {
      resolve();
      return;
    }

    // Reuse existing script tag if present (HMR safety)
    if (scriptInjected || document.querySelector('script[src*="turnstile/v0/api.js"]')) {
      const tick = setInterval(() => {
        if (window.turnstile) {
          clearInterval(tick);
          resolve();
        }
      }, 80);
      return;
    }
    scriptInjected = true;

    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => {
      // window.turnstile is set synchronously after load
      const tick = setInterval(() => {
        if (window.turnstile) {
          clearInterval(tick);
          resolve();
        }
      }, 40);
    };
    s.onerror = () => resolve(); // resolve anyway — caller handles missing turnstile
    document.head.appendChild(s);
  });

  return scriptReadyPromise;
}

export const TurnstileWidget = ({ onToken, onError }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey) {
      if (onToken) onToken("");
      return;
    }

    let cancelled = false;

    loadTurnstileScript().then(() => {
      if (cancelled) return;
      const container = containerRef.current;
      if (!container) return;
      if (!window.turnstile) {
        if (onError) onError("Verification unavailable.");
        return;
      }
      // If a widget is already inside, don't render again (StrictMode safety)
      if (container.childElementCount > 0) return;

      try {
        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: siteKey,
          appearance: "interaction-only",
          callback: (t) => {
            if (!cancelled && onToken) onToken(t);
          },
          "error-callback": () => {
            if (!cancelled && onError) onError("Verification failed. Please try again.");
          },
          "expired-callback": () => {
            if (widgetIdRef.current && window.turnstile) {
              try { window.turnstile.reset(widgetIdRef.current); } catch (_) { /* noop */ }
            }
          },
          "timeout-callback": () => {
            if (!cancelled && onError) onError("Verification timed out — please try again.");
          },
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Turnstile render failed:", e);
        if (!cancelled && onError) onError("Verification unavailable.");
      }
    });

    return () => {
      cancelled = true;
      // Intentionally NOT calling turnstile.remove() — StrictMode double-invoke
      // would race. The widget is invisible until interaction needed; letting it
      // linger across remounts is harmless and prevents the race entirely.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  return (
    <div
      ref={containerRef}
      data-testid="turnstile-widget"
      aria-hidden="true"
      style={{ display: siteKey ? "block" : "none", minHeight: 0 }}
    />
  );
};
